import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import * as Navigation from 'components/Navigation';
import FuseNavHorizontalCollapse from './horizontal/FuseNavHorizontalCollapse';
import FuseNavHorizontalGroup from './horizontal/FuseNavHorizontalGroup';
import FuseNavHorizontalItem from './horizontal/FuseNavHorizontalItem';
import FuseNavHorizontalLink from './horizontal/FuseNavHorizontalLink';
import FuseNavVerticalCollapse from './vertical/FuseNavVerticalCollapse';
import FuseNavVerticalGroup from './vertical/FuseNavVerticalGroup';
import FuseNavVerticalItem from './vertical/FuseNavVerticalItem';
import FuseNavVerticalLink from './vertical/FuseNavVerticalLink';
import FuseNavItem, { registerComponent } from './FuseNavItem';

/*
Register Fuse Navigation Components
 */
registerComponent('vertical-group', FuseNavVerticalGroup);
registerComponent('vertical-collapse', FuseNavVerticalCollapse);
registerComponent('vertical-item', FuseNavVerticalItem);
registerComponent('vertical-link', FuseNavVerticalLink);
registerComponent('vertical-parent', Navigation.Parent);
registerComponent('horizontal-group', FuseNavHorizontalGroup);
registerComponent('horizontal-collapse', FuseNavHorizontalCollapse);
registerComponent('horizontal-item', FuseNavHorizontalItem);
registerComponent('horizontal-link', FuseNavHorizontalLink);
registerComponent('vertical-divider', () => <Divider className="my-16" />);
registerComponent('horizontal-divider', () => <Divider className="my-16" />);

// const navItemHeight = 50;

const useStyles = makeStyles((theme) => ({
  navigation: {
    '& .list-item': {
      '&:hover:not(.active)': {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0,0,0,.04)',
      },
      '&:focus:not(.active)': {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0,0,0,.05)',
      },
    },
  },
  verticalNavigation: {
    '&.active-square-list': {
      '& .list-item, & .active.list-item': {
        width: '100%',
        borderRadius: '0',
      },
    },
    '&.dense': {
      '& .list-item': {
        paddingTop: 0,
        paddingBottom: 0,
        height: 32,
      },
    },
  },
  horizontalNavigation: {
    '&.active-square-list': {
      '& .list-item': {
        borderRadius: '0',
      },
    },
    '& .list-item': {
      padding: '8px 12px 8px 12px',
      height: 50,
      minHeight: 50,
      '&.level-0': {
        height: 44,
        minHeight: 44,
      },
      '& .list-item-text': {
        padding: '0 0 0 8px',
      },
    },
  },
  '@global': {
    '.popper-navigation-list': {
      '& .list-item': {
        padding: '8px 12px 8px 12px',
        height: 50,
        minHeight: 50,
        '& .list-item-text': {
          padding: '0 0 0 8px',
        },
      },
      '&.dense': {
        '& .list-item': {
          minHeight: 32,
          height: 32,
          '& .list-item-text': {
            padding: '0 0 0 8px',
          },
        },
      },
    },
  },
}));

function FuseNavigation(props) {
  const classes = useStyles(props);
  const { foldedOpen } = useSelector(({ fuse }) => fuse.navbar);
  const { navigation, layout, active, dense, className, onClick, isActive } = props;

  const verticalNav = (
    <List
      className={clsx(
        'navigation whitespace-no-wrap',
        classes.navigation,
        classes.verticalNavigation,
        `active-${active}-list`,
        dense && 'dense',
        className,
      )}
    >
      {navigation.map(
        (_item) =>
          !_item.isInvisible && (
            <React.Fragment key={_item.id}>
              {!foldedOpen && _item.type !== 'parent' ? (
                <Tooltip title={_item.title || ''} placement="right" enterDelay={2000} enterNextDelay={2000}>
                  <span>
                    <FuseNavItem
                      type={`vertical-${_item.type}`}
                      item={_item}
                      nestedLevel={0}
                      onClick={onClick}
                      isActive={isActive}
                    />
                  </span>
                </Tooltip>
              ) : (
                <FuseNavItem
                  type={`vertical-${_item.type}`}
                  item={_item}
                  nestedLevel={0}
                  onClick={onClick}
                  isActive={isActive}
                />
              )}
            </React.Fragment>
          ),
      )}
    </List>
  );

  const horizontalNav = (
    <List
      className={clsx(
        'navigation whitespace-no-wrap flex p-0',
        classes.navigation,
        classes.horizontalNavigation,
        `active-${active}-list`,
        dense && 'dense',
        className,
      )}
    >
      {navigation.map(
        (_item) =>
          !_item.isInvisible && (
            <Tooltip key={_item.id} title={_item.title || ''} placement="right" enterDelay={2000} enterNextDelay={2000}>
              {!foldedOpen ? (
                <span>
                  <FuseNavItem type={`horizontal-${_item.type}`} item={_item} nestedLevel={0} dense={dense} />
                </span>
              ) : (
                <FuseNavItem type={`horizontal-${_item.type}`} item={_item} nestedLevel={0} dense={dense} />
              )}
            </Tooltip>
          ),
      )}
    </List>
  );

  if (navigation.length > 0) {
    switch (layout) {
      case 'horizontal': {
        return horizontalNav;
      }
      case 'vertical':
      default: {
        return verticalNav;
      }
    }
  } else {
    return null;
  }
}

FuseNavigation.propTypes = {
  navigation: PropTypes.array.isRequired,
};

FuseNavigation.defaultProps = {
  layout: 'vertical',
};

export default React.memo(FuseNavigation);
